import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Table, Button, Modal, Form, Badge, Card, Row, Col, InputGroup } from "react-bootstrap";

import TableHeader from "../common/TableHeader";
import DataApi from "../../api/dataApi";
import ResizableTable from "../common/ResizableTable";
import PermissionDenied from "../../utils/permission_denied";

const Payment = ({permissions}) => {


  console.log("permissions=>",permissions)
  
  
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedInvoice, setExpandedInvoice] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  
  // 1. Filter State
  const [filters, setFilters] = useState({
    invoice_number: "",
    vendor_name: "",
    payment_mode: "",
    from_date: "",
    to_date: ""
  });

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const recordsPerPage = 10;

  const [formData, setFormData] = useState({
    amount_paid: "",
    payment_date: new Date().toISOString().split('T')[0],
    payment_mode: "UPI",
    transaction_id: "",
    remarks:""
  });

  useEffect(() => {
    loadInvoices();
    loadPayments();
  }, []);

  const loadInvoices = async () => {
    try {
      const res = await new DataApi("invoice").fetchAll();
      const responseData = res?.data;
      const data = responseData?.data || responseData || [];
      setInvoices(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Error loading invoices:", error);
    } finally {
      setLoading(false);
    }
  };

  const loadPayments = async () => {
    try {
      const res = await new DataApi("payment").fetchAll();
      const responseData = res?.data;
      const data = responseData?.data || responseData || [];
      setPayments(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Error loading payments:", error);
    }
  };

  // 2. Extract Unique Vendors for Filter Dropdown
  const vendorOptions = useMemo(() => {
    const names = invoices.map(inv => inv.vendor_name).filter(Boolean);
    return [...new Set(names)];
  }, [invoices]);

  // 3. Logic to filter invoices based on the Filter Bar
  const filteredInvoices = useMemo(() => {
    return invoices.filter((inv) => {
      const matchNum = inv.invoice_number?.toLowerCase().includes(filters.invoice_number.toLowerCase());
      const matchVendor = !filters.vendor_name || inv.vendor_name === filters.vendor_name;
      const matchDate = (!filters.from_date || new Date(inv.invoice_date) >= new Date(filters.from_date)) &&
                        (!filters.to_date || new Date(inv.invoice_date) <= new Date(filters.to_date));
      
      // Check if any payment mode matches within the associated payments
      const invPayments = payments.filter(p => p.invoice_id === inv.id);
      const matchMode = !filters.payment_mode || invPayments.some(p => p.payment_mode === filters.payment_mode);

      return matchNum && matchVendor && matchDate && matchMode;
    });
  }, [invoices, payments, filters]);

  const toggleRow = (invoiceId) => {
    setExpandedInvoice(expandedInvoice === invoiceId ? null : invoiceId);
  };

  // Navigate to PaymentDetail page with invoice data
  const navigateToPaymentDetail = (record) => {
    // Pass the invoice and its payments via state
    const invoicePayments = getPaymentsByInvoice(record.id);
    navigate(`/payment/${record.id}`, { 
      state: { 
        invoice: record,
        payments: invoicePayments
      } 
    });
  };

  const openPaymentModal = (invoice) => {
    setSelectedInvoice(invoice);
    setFormData({ 
        ...formData, 
        amount_paid: invoice.due_amount,
        remarks: "" 
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    const payload = { ...formData, invoice_id: selectedInvoice.id };
    await new DataApi("payment").post("", payload);
    setShowModal(false);
    loadPayments();
    loadInvoices();
  };

  const getPaymentsByInvoice = (invoiceId) => {
    return payments.filter((p) => p.invoice_id === invoiceId);
  };

  const formatCurrency = (val) => 
    new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(val || 0);

  const columns = useMemo(() => [
    {
      field: "invoice_number",
      label: "Invoice Details",
      minWidth: 160,
      render: (value, record) => (
        <div className="d-flex align-items-center" onClick={() => navigateToPaymentDetail(record)} style={{ cursor: "pointer" }}>
          <div className="me-2 text-muted">
            {/* {expandedInvoice === record.id ? <ChevronUp size={16} /> : <ChevronDown size={16} />} */}
            
          </div>
          <div>
            <span className="fw-bold text-primary">{value}</span>
            <div className="small text-muted">Date: {new Date(record.invoice_date).toLocaleDateString()}</div>
          </div>
        </div>
      )
    },
    {
      field: "vendor_name",
      label: "Vendor",
      minWidth: 150,
      render: (val) => <span className="fw-medium text-dark">{val || "N/A"}</span>
    },
    {
      field: "total_amount",
      label: "Total",
      minWidth: 120,
      render: (value) => <span className="fw-semibold">{formatCurrency(value)}</span>
    },
    {
      field: "paid_amount",
      label: "Paid",
      minWidth: 120,
      render: (value) => <span className="text-success fw-medium">{formatCurrency(value)}</span>
    },
    {
      field: "due_amount",
      label: "Due",
      minWidth: 120,
      render: (value) => <span className="text-danger fw-medium">{formatCurrency(value)}</span>
    },
    {
      field: "status",
      label: "Status",
      minWidth: 120,
      render: (value) => (
        <Badge 
          pill 
          className={`px-3 py-2 fw-normal ${
            value === "Paid" ? "bg-success-soft text-success" : 
            value === "Partially Paid" ? "bg-warning-soft text-warning" : "bg-danger-soft text-danger"
          }`}
        >
          {value}
        </Badge>
      )
    },
    {
      field: "actions",
      label: "Actions",
      minWidth: 110,
      align: "center",
      render: (_, record) => (
        <div className="">
          {record.due_amount > 0 && (
            <Button 
              variant="primary" 
              size="sm" 
              className="px-3 shadow-sm d-inline-flex align-items-center gap-1"
              onClick={() => openPaymentModal(record)}
            >
          <span style={{fontSize: '0.875rem'}}>Pay</span>
         </Button>
          )}
        </div>
      )
    }
  ], [expandedInvoice]);


  if (!permissions?.allowView) {
    return <PermissionDenied />;
  }


  return (
    <div className="p-4" style={{ backgroundColor: "#f8f9fa", minHeight: "100vh" }}>
      <TableHeader icon='fa fa-inr' title={'Invoice Payment Management'} />

      {/* 1. Filter Bar (Styled per screenshot) */}
      <Card className="border-0 shadow-sm mb-4">
        <Card.Body className="p-3"  style={{ background: "var(--primary-background-color)", color: "var(--primary-color)" }}>
          <Row className="g-3 align-items-end">
            <Col md={3}>
              <Form.Label className="small fw-bold text-muted mb-1 d-flex align-items-center">
<span className="me-1" style={{fontSize: '0.875rem'}}>🔍</span>
              </Form.Label>
              <Form.Control 
                placeholder="Search invoice..." 
                className="border-0 shadow-sm py-2"
                value={filters.invoice_number}
                onChange={(e) => setFilters({...filters, invoice_number: e.target.value})}
              />
            </Col>
            <Col md={2}>
              <Form.Label className="small fw-bold text-muted mb-1 d-flex align-items-center">
<span className="me-1" style={{fontSize: '0.875rem'}}>👤</span>
              </Form.Label>
              <Form.Select 
                className="border-0 shadow-sm py-2"
                value={filters.vendor_name}
                onChange={(e) => setFilters({...filters, vendor_name: e.target.value})}
              >
                <option value="">All Vendors</option>
                {vendorOptions.map(v => <option key={v} value={v}>{v}</option>)}
              </Form.Select>
            </Col>
            <Col md={2}>
              <Form.Label className="small fw-bold text-muted mb-1 d-flex align-items-center">
                <span className="me-1" style={{fontSize: '0.875rem'}}>⏳</span> Payment Mode
              </Form.Label>
              <Form.Select 
                className="border-0 shadow-sm py-2"
                value={filters.payment_mode}
                onChange={(e) => setFilters({...filters, payment_mode: e.target.value})}
              >
                <option value="">All Modes</option>
                <option>Cash</option>
                <option>UPI</option>
                <option>Cheque</option>
                <option>Others</option>
              </Form.Select>
            </Col>
            <Col md={2}>
              <Form.Label className="small fw-bold text-muted mb-1 d-flex align-items-center">
                <span className="me-1" style={{fontSize: '0.875rem'}}>📅</span> Date From
              </Form.Label>
              <Form.Control 
                type="date" 
                className="border-0 shadow-sm py-2"
                value={filters.from_date}
                onChange={(e) => setFilters({...filters, from_date: e.target.value})}
              />
            </Col>
            <Col md={2}>
              <Form.Label className="small fw-bold text-muted mb-1 d-flex align-items-center">
                <span className="me-1" style={{fontSize: '0.875rem'}}>📅</span> Date To
              </Form.Label>
              <Form.Control 
                type="date" 
                className="border-0 shadow-sm py-2"
                value={filters.to_date}
                onChange={(e) => setFilters({...filters, to_date: e.target.value})}
              />
            </Col>
            <Col md={1}>
              <Button 
                variant="outline-primary" 
                className="border-0 shadow-sm py-2"
                onClick={() => setFilters({ invoice_number: "", vendor_name: "", payment_mode: "", from_date: "", to_date: "" })}
              >
                <i className="fa fa-sync" aria-hidden="true"></i>
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* 2. Main Table */}
      <Card className="border-0 shadow-sm overflow-hidden mb-4">
        <ResizableTable
          data={filteredInvoices}
          columns={columns}
          loading={loading}
          currentPage={currentPage}
          recordsPerPage={recordsPerPage}
          onPageChange={setCurrentPage}
          showActions={false}
          showSerialNumber={true}
        />
        
        {/* 3. Expanded Payment Section */}
        {expandedInvoice && (
          <div className="bg-light p-4 border-top">
            <div className="d-flex align-items-center mb-3">
              <div className="bg-primary bg-opacity-10 p-2 rounded me-2">
                <span className="text-primary" style={{fontSize: '1.125rem'}}>📜</span>
              </div>
              <h6 className="mb-0 fw-bold">Payment History: {invoices.find(i => i.id === expandedInvoice)?.invoice_number}</h6>
            </div>
            
            <Table responsive borderless className="bg-white rounded shadow-sm mb-0 overflow-hidden">
              <thead className="bg-light text-muted small fw-bold text-uppercase">
                <tr>
                  <th className="ps-3 py-3">Date</th>
                  <th className="py-3">Mode</th>
                  <th className="py-3">Transaction ID</th>
                  <th className="py-3">Amount Paid</th>
                  <th className="py-3 pe-3">Remarks</th>
                </tr>
              </thead>
              <tbody className="small">
                {getPaymentsByInvoice(expandedInvoice).map((p) => (
                  <tr key={p.id} className="border-top">
                    <td className="ps-3 py-3">{new Date(p.payment_date).toLocaleDateString('en-GB')}</td>
                    <td className="py-3"><Badge bg="secondary" className="fw-normal">{p.payment_mode}</Badge></td>
                    <td className="py-3 text-monospace">{p.transaction_id || "—"}</td>
                    <td className="py-3 fw-bold text-primary">{formatCurrency(p.amount_paid)}</td>
                    <td className="py-3 pe-3 text-muted">{p.remarks || "—"}</td>
                  </tr>
                ))}
                {getPaymentsByInvoice(expandedInvoice).length === 0 && (
                  <tr><td colSpan={5} className="text-center py-4 text-muted">No payments recorded yet.</td></tr>
                )}
              </tbody>
            </Table>
          </div>
        )}
      </Card>

      {/* 4. Payment Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} centered backdrop="static" contentClassName="border-0 shadow-lg">
        <Modal.Header closeButton style={{ background: "var(--secondary-color, #f8f9fa)", color: "var(--primary-color, #000)" }}>
          <Modal.Title className="h5 fw-bold">Record Payment</Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4">
          <div className="bg-primary bg-opacity-10 p-3 rounded mb-4 d-flex justify-content-between align-items-center border-start border-primary border-4">
             <div>
                <small className="text-muted d-block">Invoice Reference</small>
                <span className="fw-bold fs-5">#{selectedInvoice?.invoice_number}</span>
             </div>
             <div className="text-end">
                <small className="text-muted d-block">Due Balance</small>
                <span className="fw-bold text-danger fs-5">{formatCurrency(selectedInvoice?.due_amount)}</span>
             </div>
          </div>

          <Form>
            <Form.Group className="mb-3">
              <Form.Label className="small fw-bold">Amount to Pay</Form.Label>
              <InputGroup>
                <InputGroup.Text className="bg-white border-end-0">₹</InputGroup.Text>
                <Form.Control
                  type="number"
                  placeholder="0.00"
                  className="border-start-0 py-2"
                  value={formData.amount_paid}
                  onChange={(e) => setFormData({ ...formData, amount_paid: e.target.value })}
                />
              </InputGroup>
            </Form.Group>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label className="small fw-bold">Payment Date</Form.Label>
                  <Form.Control
                    type="date"
                    className="py-2"
                    value={formData.payment_date}
                    onChange={(e) => setFormData({ ...formData, payment_date: e.target.value })}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label className="small fw-bold">Mode</Form.Label>
                  <Form.Select
                    className="py-2"
                    value={formData.payment_mode}
                    onChange={(e) => setFormData({ ...formData, payment_mode: e.target.value })}
                  >
                    <option>Cash</option>
                    <option>Cheque</option>
                    <option>UPI</option>
                    <option>Bank Transfer</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label className="small fw-bold">Transaction ID</Form.Label>
                  <Form.Control
                    type="text"
                    className="py-2"
                    placeholder="Ref#"
                    value={formData.transaction_id}
                    onChange={(e) => setFormData({ ...formData, transaction_id: e.target.value })}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label className="small fw-bold">Remark</Form.Label>
                  <Form.Control
                    type="text"
                    className="py-2"
                    placeholder="Internal note"
                    value={formData.remarks}
                    onChange={(e) => setFormData({ ...formData, remarks: e.target.value })}
                  />
                </Form.Group>
              </Col>
            </Row>
          </Form>
        </Modal.Body>
        <Modal.Footer className="bg-light border-0">
          <Button variant="primary" onClick={() => setShowModal(false)}>Cancel</Button>
          <Button variant="" className="px-2 shadow-sm" style={{background:"var(--primary-color)", color:"#fff"}} onClick={handleSave}>Confirm Payment</Button>
        </Modal.Footer>
      </Modal>

      <style>{`
        .bg-success-soft { background-color: #d1e7dd !important; color: #0f5132 !important; }
        .bg-warning-soft { background-color: #fff3cd !important; color: #664d03 !important; }
        .bg-danger-soft { background-color: #f8d7da !important; color: #842029 !important; }
        .text-monospace { font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace; }
        .form-control:focus, .form-select:focus { box-shadow: 0 0 0 0.25rem rgba(13, 110, 253, 0.1); border-color: #dee2e6; }
      `}</style>
    </div>
  );
};

export default Payment;
